/**
 * @file ClientSettingIndividualMenus.js
 * @description This file contains the ClientSettingIndividualMenus component for renaming Individual menu titles (Benefits, Assets, Messages).
 */

import { useState, useEffect } from 'react';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
    deleteClientOption,
} from '../../../services/clientService';
import Toast from '../../ui/toasts/Toast';
import {
    SecurityAttributes,
    ClientOptionEnum,
} from '../../../utils/data/enums';
import { useAuthContext } from '../../../store/Contexts';
import { getText } from '../../../i18n';
import ClientOptionButtons from './ClientOptionButtons';

const MAX_TITLE_LENGTH = 15;

const FIELD_CONFIG = [
    {
        label: getText('msg_927f40673764'),
        fieldName: 'benefits',
        optionId: ClientOptionEnum.RenameBenefitsMenu,
    },
    {
        label: getText('msg_20e338624cee'),
        fieldName: 'assets',
        optionId: ClientOptionEnum.RenameAssetsMenu,
    },
    {
        label: getText('msg_f1702b468627'),
        fieldName: 'messages',
        optionId: ClientOptionEnum.RenameMessagesMenu,
    },
];

const EMPTY_TITLES = { benefits: '', assets: '', messages: '' };

const inputStyle = {
    height: '48px',
    background: '#FFFFFF',
    border: '1px solid rgba(47, 43, 67, 0.2)',
    boxShadow:
        '0px 1px 3px rgba(47, 43, 67, 0.1), inset 0px -1px 0px rgba(47, 43, 67, 0.1)',
    borderRadius: '12px',
    padding: '0px 16px',
    width: '100%',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '24px',
    outline: 'none',
    boxSizing: 'border-box',
};

const labelStyle = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '24px',
    color: '#2F2B43',
    display: 'block',
    marginBottom: '8px',
};

const charLimitStyle = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: 'var(--iNGAGED-Neutral-Color2)',
    marginTop: '8px',
};

const ClientSettingIndividualMenus = () => {
    const [menuTitles, setMenuTitles] = useState(EMPTY_TITLES);
    const [initialObjects, setInitialObjects] = useState({
        benefits: null,
        assets: null,
        messages: null,
    });

    const { hasAttribute, reloadMenuLabels } = useAuthContext();

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const data = await getClientOptions(0, [
                    ClientOptionEnum.RenameBenefitsMenu,
                    ClientOptionEnum.RenameAssetsMenu,
                    ClientOptionEnum.RenameMessagesMenu,
                ]);

                const newObjects = {
                    benefits: null,
                    assets: null,
                    messages: null,
                };
                const newTitles = { ...EMPTY_TITLES };

                FIELD_CONFIG.forEach(({ fieldName, optionId }) => {
                    const obj = data.find(
                        item => item.clientOptionId === optionId,
                    );
                    if (obj) {
                        newObjects[fieldName] = obj;
                        newTitles[fieldName] = obj.usingDefaultValue
                            ? ''
                            : obj.value || '';
                    }
                });

                setInitialObjects(newObjects);
                setMenuTitles(newTitles);
            } catch (error) {
                console.error(
                    'Could not fetch individual menus settings:',
                    error,
                );
            }
        };

        fetchOptions();
    }, []);

    function handleTitleChange(fieldName, value) {
        setMenuTitles(previous => ({
            ...previous,
            [fieldName]: value.slice(0, MAX_TITLE_LENGTH),
        }));
    }

    function handleCancel() {
        const resetTitles = {};
        FIELD_CONFIG.forEach(({ fieldName }) => {
            const obj = initialObjects[fieldName];
            resetTitles[fieldName] =
                obj && !obj.usingDefaultValue ? obj.value || '' : '';
        });
        setMenuTitles(resetTitles);
    }

    async function saveMenuField(fieldName, optionId) {
        const initialObj = initialObjects[fieldName];
        const currentValue = menuTitles[fieldName];
        const initialValue =
            initialObj && !initialObj.usingDefaultValue
                ? initialObj.value || ''
                : '';

        if (currentValue === initialValue) {
            return false;
        }

        if (!currentValue && initialObj && !initialObj.usingDefaultValue) {
            const deleteResponse = await deleteClientOption(0, optionId);

            if (deleteResponse?.error) {
                Toast.error(
                    `There was an error attempting to reset the ${fieldName} menu title.`,
                );
                throw new Error(`Reset ${fieldName} menu title failed`);
            }

            const [refetchedOption] = await getClientOptions(0, [optionId]);

            setInitialObjects(previous => ({
                ...previous,
                [fieldName]: refetchedOption || null,
            }));

            return true;
        }

        const option = { clientOptionId: optionId, value: currentValue };

        let response;
        if (!initialObj || initialObj.usingDefaultValue) {
            response = await createClientOption(option, 0);
        } else {
            response = await updateClientOption(option, 0);
        }

        if (response.error) {
            Toast.error(
                `There was an error attempting to save the ${fieldName} menu title.`,
            );
            throw new Error(`Save ${fieldName} menu title failed`);
        }

        setInitialObjects(previous => ({
            ...previous,
            [fieldName]: response.data,
        }));
        return true;
    }

    async function handleSave() {
        try {
            const results = await Promise.all(
                FIELD_CONFIG.map(({ fieldName, optionId }) =>
                    saveMenuField(fieldName, optionId),
                ),
            );

            const anyChanged = results.some(Boolean);
            if (anyChanged) {
                await reloadMenuLabels();
                Toast.success(getText('msg_8914097db913'));
            } else {
                Toast.warning(getText('msg_13c3a7ac4e47'));
            }
        } catch {
            // individual field errors are already toasted inside saveMenuField
        }
    }

    function renderRenameInput({ label, fieldName }) {
        return (
            <div
                key={fieldName}
                style={{ display: 'flex', flexDirection: 'column' }}
            >
                <div htmlFor={fieldName} style={labelStyle}>
                    {label}
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <input
                        type="text"
                        id={fieldName}
                        name={fieldName}
                        placeholder={getText('msg_5c88327fb2b0')}
                        maxLength={MAX_TITLE_LENGTH}
                        value={menuTitles[fieldName]}
                        onChange={event =>
                            handleTitleChange(fieldName, event.target.value)
                        }
                        style={inputStyle}
                    />
                    <div style={charLimitStyle}>
                        {`Character Limit ${menuTitles[fieldName].length}/${MAX_TITLE_LENGTH}`}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0px 40px',
                gap: '34px',
            }}
        >
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div>{getText('msg_f721ba9e24d1')}</div>
                    <div
                        style={{
                            color: 'var(--iNGAGED-Neutral-Color2)',
                        }}
                    >
                        {getText('msg_4085853bfdd7')}
                    </div>
                    <div
                        style={{
                            color: 'var(--iNGAGED-Neutral-Color2)',
                        }}
                    >
                        {getText('msg_e523e27af34f')}
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        width: '100%',
                    }}
                >
                    {FIELD_CONFIG.map(renderRenameInput)}
                </div>
            </div>

            {hasAttribute(SecurityAttributes.ClientSettingsFullAccess) && (
                <ClientOptionButtons
                    handleCancel={handleCancel}
                    handleSave={handleSave}
                />
            )}
        </div>
    );
};

export default ClientSettingIndividualMenus;
